import React, { useState } from 'react';
import {
  Card,
  Grid,
  Avatar,
  Typography,
  Box,
  Dialog,
  IconButton,
  Tooltip,
  Button,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory } from 'react-router-dom';
import { RWebShare } from 'react-web-share';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import { getAdvisorImagePath } from '../../services/utils';
import { getAdvisorQueryParams } from '../../utils/queryParams';
import { ReactComponent as LinkedInIcon } from '../../icons/linkedin-full.svg';
import { ReactComponent as ReplyIcon } from '../../icons/reply.svg';
import {
  isLinkedinAuthenticated,
  authorizeWithPopUp,
} from '../../services/LinkedIn';
import LinkedInPostModal from '../../components/LinkedInPostModal';

export default function BoardroomHeader({
  advisor,
  setSuccessSnackbar,
  setSuccessMessage,
}) {
  const { accountDetails, isFreeTrial, isFreemium } = useAccount();
  const [open, setOpen] = useState(false);
  let url = 'https://register.advisorycloud.com/platform/board-matching/e.html';
  const publicProfileFlag = false;
  const publicProfile = publicProfileFlag
    ? `https://${process.env.REACT_APP_PUBLIC_PROFILES_URL}/${advisor.publicProfileUrl}`
    : `advisorycloud.com/profile/${advisor.publicProfileUrl}`;
  const [linkedInPostModal, setLinkedInPostModal] = useState(false);
  const isWrapped = useMediaQuery('(min-width:1279px) and (max-width:1450px)');
  const isMobile = useMediaQuery('(max-width:1279px)');
  const history = useHistory();

  function copyToClipboard() {
    navigator.clipboard.writeText(publicProfile);
    setSuccessMessage('Profile link has been copied to your clipboard.');
    setSuccessSnackbar(true);
  }

  async function linkedInSign() {
    const isAuthenticatedLinkedIn = isLinkedinAuthenticated();
    if (isAuthenticatedLinkedIn) {
      setLinkedInPostModal(true);
    } else {
      await authorizeWithPopUp();
    }
  }

  async function linkedInResponse(linkedinResponse) {
    if (linkedinResponse === 201) {
      setSuccessMessage('Your profile is shared successfully.');
      setSuccessSnackbar(true);
    } else if (linkedinResponse === 422) {
      setSuccessMessage(
        'You have already posted this content, either update the post or try again in 10 minutes'
      );
      setSuccessSnackbar(true);
    } else {
      setSuccessMessage('An error occurred while sharing your profile.');
      setSuccessSnackbar(true);
    }
  }

  const onClickHeadline = () =>
    eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
      ...accountDetails,
      openInEdit: true,
      isFocus: true,
    });

  const onClick = () =>
    eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
      ...accountDetails,
      openInEdit: false,
    });

  return (
    <>
      <Card
        sx={{
          p: 3,
          borderRadius: '8px',
          boxShadow: 'none',
          // marginTop: { xxs: '30px', sm: '0px' },
          mt: 0,
        }}
      >
        <Grid container spacing={3} alignItems="center" justifyContent="center">
          <Grid item sx={{ pl: isWrapped ? 0 : 2 }}>
            <Box
              onClick={onClick}
              id="advisor-boardroom-avatar"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: { xxs: 128, xs: isWrapped ? 150 : 207 },
                height: { xxs: 128, xs: 207 },
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              <Avatar
                alt={`${advisor.givenName} Logo`}
                src={getAdvisorImagePath(advisor)}
                sx={{
                  width: { xxs: 100, xs: 150 },
                  height: { xxs: 100, xs: 150 },
                  borderRadius: '8px',
                }}
              />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography
              id="boardroom-advisor-name"
              sx={{
                fontWeight: '600',
                fontSize: { xxs: 24, xs: 40 },
                textAlign: { xxs: 'center', xs: 'left' },
                lineHeight: '41px',
                color: '#001030',
                display: 'flex',
              }}
            >
              Welcome,
              <Typography
                component="span"
                sx={{
                  fontWeight: '600',
                  fontSize: { xxs: 24, xs: 40 },
                  textAlign: { xxs: 'center', xs: 'left' },
                  lineHeight: '41px',
                  color: '#2B71F6',
                  marginLeft: '7px',
                }}
              >
                {advisor.givenName}.
              </Typography>
            </Typography>

            <Box
              id="advisor-boardroom-current-goal"
              onClick={advisor.headline ? onClick : onClickHeadline}
              sx={{
                bgcolor: '#F1F9FF',
                borderRadius: '8px',
                py: '5px',
                pl: '10px',
                pr: '2px',
                width: '100%',
                borderTop: '1px solid #4FB5F9',
                borderRight: '1px solid #4FB5F9',
                borderBottom: '1px solid #4FB5F9',
                borderLeft: '5px solid #4FB5F9',
                marginTop: '15px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  fontWeight: '400',
                  fontSize: { xxs: 12, xs: 14 },
                  color: '#00548B',
                  lineHeight: { xxs: '18px', xs: '21px' },
                }}
              >
                {advisor.headline ||
                  'Click here to create a 1-2 sentence description of your professional interests and expertise.'}
              </Typography>

              <EditIcon
                sx={{
                  cursor: 'pointer',
                  color: '#00548B',
                  height: '15px',
                  marginTop: '3px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickHeadline();
                }}
              />
            </Box>

            <Box
              sx={{
                display: { xxs: 'block', xs: 'flex' },
                justifyContent: 'flex-start',
                mt: 2,
                gap: isWrapped ? 1 : 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: '700',
                  fontSize: '12px',
                  lineHeight: '150%',
                  letterSpacing: '0%',
                  color: '#4D586F',
                  marginTop: '8px',
                }}
              >
                AdvisoryCloud Profile URL:
              </Typography>
              <Button
                variant="outlined"
                id="profile-url-button"
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  border: '1px solid #EFF0F7',
                  whiteSpace: 'nowrap',
                  height: '35px',
                  color: '#232B35',
                  fontSize: { xxs: 12, xs: 14 },
                  fontWeight: '400',
                  overflow: 'hidden',
                  maxWidth: {
                    xxs: publicProfile.length > 40 ? '350px' : 'none',
                    xs: isWrapped ? '290px' : 'none',
                  },
                }}
                onClick={copyToClipboard}
              >
                {publicProfile}
              </Button>
              {!isMobile && (
                <Tooltip
                  title="We recommend you share your Advisor Profile URL wherever you have the most professional contacts or visibility to help attract advisory work and connections you care about."
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: '#f2f2f2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconButton size="small" sx={{ color: '#666', p: 0 }}>
                      i
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
            </Box>
            <Box
              sx={{
                display: { xxs: 'block', xs: 'flex' },
                justifyContent: 'flex-start',
                mt: 2,
                gap: isWrapped ? 1 : 2,
              }}
            >
              <RWebShare
                data={{
                  text: `I'm now available as an advisor on @advisorycloud. You can request a meeting with me here if you're ever seeking my advice.`,
                  url: advisor.publicProfileUrl,
                  title: 'Share your advisor profile',
                }}
                onClick={() => {
                  setSuccessMessage('Your profile is shared successfully.');
                  setSuccessSnackbar(true);
                }}
              >
                <Button
                  variant="text"
                  id="shareButton"
                  sx={{
                    textTransform: 'none',
                    color: '#232B35',
                    fontFamily: 'Poppins',
                    fontWeight: '500',
                    fontSize: { xxs: 12, xs: 14 },
                  }}
                  startIcon={<ReplyIcon />}
                >
                  Share
                </Button>
              </RWebShare>

              <Button
                variant="text"
                id="promoteButton"
                sx={{
                  textTransform: 'none',
                  color: '#232B35',
                  fontFamily: 'Poppins',
                }}
                startIcon={
                  <LinkedInIcon
                    style={{
                      width: '20px',
                      height: '20px',
                      // marginRight: '5px',
                      color: '#0077B5',
                    }}
                  />
                }
                onClick={linkedInSign}
              >
                Promote
              </Button>
              <Button
                variant="text"
                id="promoteButton"
                sx={{
                  textTransform: 'none',
                  color: '#232B35',
                  fontFamily: 'Poppins',
                }}
                startIcon={'🚀'}
                onClick={() => {
                  history.push('/ub/advisor/services/social');
                }}
              >
                Social Boost
              </Button>
              <Button
                variant="text"
                id="promoteButton"
                sx={{
                  textTransform: 'none',
                  color: '#232B35',
                  fontFamily: 'Poppins',
                }}
                startIcon={'💬'}
                onClick={() => {
                  history.push('/ub/get-assistance');
                }}
              >
                Get Assistance
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            width: { xxs: '100%', sm: '800px' },
            height: { xxs: '100vh', sm: '600px' },
            maxWidth: { xs: '100%', sm: '800px' },
            maxHeight: { xs: '100vh', sm: '600px' },
            borderRadius: { xxs: 0, sm: '10px' },
            overflow: 'hidden',
            position: 'relative',
          },
        }}
      >
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#777',
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        <iframe
          src={`${url}?${getAdvisorQueryParams({
            accountDetails,
            isFreeTrial,
            isFreemium,
          })}`}
          title="OptimizeMyProfileForBoards"
          frameBorder="0"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </Dialog>
      {linkedInPostModal ? (
        <>
          <LinkedInPostModal
            linkedInPostModal={linkedInPostModal}
            setLinkedInPostModal={setLinkedInPostModal}
            linkedInResponse={linkedInResponse}
            fontFamily={'Poppins'}
            shareProfile
          />
        </>
      ) : null}
    </>
  );
}
